import { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "features/store";
import { useViewAs } from "hooks/useViewAs";
import {
  isManager as isManagerRole,
  isPartnershipTeam as isPartnershipTeamRole,
  isSuperAdmin,
} from "utils/roles";
import { pushNotification } from "features/notificationSlice";
import { useMediaQuery, useTheme } from "@mui/material";
import {
  Add,
  Delete,
  Edit,
  Info,
  OpenInNew,
  Star,
  StarBorder,
  Tune,
  Warning,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import PartnerBadge from "components/partners/PartnerBadge";
import PaginationControls from "components/fileStorage/PaginationControls";
import FileSearch from "components/fileStorage/FileSearch";
import CustomScrollbars from "components/CustomScrollbars";
import AppDialog from "components/common/AppDialog";
import { TABLE_PAGE_CONTAINER_HEIGHT } from "config";
import {
  useCreatePartnerMutation,
  useDeletePartnerMutation,
  useGetPartnersAdminQuery,
  useTogglePartnerFeaturedMutation,
  useUpdatePartnerMutation,
  usePresignPartnerLogoUploadMutation,
  useFinalizePartnerLogoUploadMutation,
} from "features/partners/partnersApi";
import Layout from "components/Layout";
import {
  INDUSTRY_DISPLAY_NAMES,
  PARTNER_TYPE_DISPLAY_NAMES,
  RELATIONSHIP_TILES,
  type IndustryType,
  type PartnerAdmin,
  type PartnerType,
  type RelationshipTileKey,
} from "types/partners";

interface ConfirmAccessChangesSection {
  label: string;
  items?: string[];
  added?: string[];
  updated?: string[];
  removed?: string[];
  enabled?: string[];
  disabled?: string[];
}

interface PartnerFormData {
  name: string;
  partner_type: PartnerType;
  industry_type: IndustryType;
  tile_description: string;
  details_description: string;
  partnership_description: string;
  relationship_tiles: RelationshipTileKey[];
  is_featured: boolean;
  is_active: boolean;
  logo_s3_path: string;
  logo_file: string;
  logo_url: string;
}

const INITIAL_FORM_DATA: PartnerFormData = {
  name: "",
  partner_type: "service",
  industry_type: "payroll_hcm",
  tile_description: "",
  details_description: "",
  partnership_description: "",
  relationship_tiles: [],
  is_featured: false,
  is_active: true,
  logo_s3_path: "",
  logo_file: "",
  logo_url: "",
};

export default function PartnerManagementPage() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState<
    "name" | "type" | "industry" | "featured" | "active" | undefined
  >(undefined);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPartner, setEditingPartner] = useState<PartnerAdmin | null>(
    null,
  );
  const [formData, setFormData] = useState<PartnerFormData>(INITIAL_FORM_DATA);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [featuredWarningOpen, setFeaturedWarningOpen] = useState(false);
  const [pendingFeaturedChange, setPendingFeaturedChange] = useState<
    boolean | null
  >(null);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteConfirmInput, setDeleteConfirmInput] = useState("");
  const [partnerToDelete, setPartnerToDelete] = useState<PartnerAdmin | null>(
    null,
  );
  const [logoDuplicateWarningOpen, setLogoDuplicateWarningOpen] =
    useState(false);
  const [pendingLogoFile, setPendingLogoFile] = useState<File | null>(null);

  const [typeFilter, setTypeFilter] = useState<PartnerType | "">("");
  const [relationshipTilesFilter, setRelationshipTilesFilter] = useState<
    RelationshipTileKey | ""
  >("");
  const [industryFilter, setIndustryFilter] = useState<IndustryType | "">("");
  const [mobileOptionsDrawerOpen, setMobileOptionsDrawerOpen] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.auth.user);
  const { profile: platformProfile, readOnly } = useViewAs();
  const isSuperUser = isSuperAdmin(platformProfile);
  const isCalibrateManager = isManagerRole(platformProfile);
  const isPartnershipTeam = isPartnershipTeamRole(platformProfile);
  const canEditPartners = (isSuperUser || isPartnershipTeam) && !readOnly;

  const hasUnsavedChanges = useMemo(() => {
    if (!editingPartner) {
      return (
        formData.name.trim() !== "" ||
        formData.tile_description.trim() !== "" ||
        formData.details_description.trim() !== "" ||
        formData.partnership_description.trim() !== "" ||
        formData.relationship_tiles.length > 0 ||
        logoFile !== null
      );
    }
    return (
      formData.name !== editingPartner.name ||
      formData.partner_type !== editingPartner.partner_type ||
      formData.industry_type !== editingPartner.industry_type ||
      formData.tile_description !== editingPartner.tile_description ||
      formData.details_description !== editingPartner.details_description ||
      formData.partnership_description !==
        editingPartner.partnership_description ||
      JSON.stringify(Array.from(formData.relationship_tiles).sort()) !==
        JSON.stringify(Array.from(editingPartner.relationship_tiles).sort()) ||
      formData.is_featured !== editingPartner.is_featured ||
      formData.is_active !== editingPartner.is_active ||
      logoFile !== null
    );
  }, [formData, editingPartner, logoFile]);

  const isFormValid = useMemo(() => {
    return (
      formData.name.trim() !== "" &&
      formData.partner_type &&
      formData.industry_type &&
      formData.tile_description.trim() !== "" &&
      formData.details_description.trim() !== "" &&
      formData.partnership_description.trim() !== "" &&
      formData.relationship_tiles.length > 0
    );
  }, [formData]);

  const SORT_FIELD_MAP: Record<NonNullable<typeof sortBy>, string> = {
    name: "name",
    type: "partner_type",
    industry: "industry_type",
    featured: "is_featured",
    active: "is_active",
  };
  const ordering = sortBy
    ? `${sortOrder === "desc" ? "-" : ""}${SORT_FIELD_MAP[sortBy]}`
    : undefined;

  const { data, isLoading, isFetching, refetch } = useGetPartnersAdminQuery({
    search: search || undefined,
    partner_type: typeFilter || undefined,
    industry_type: industryFilter || undefined,
    relationship_tile: relationshipTilesFilter || undefined,
    ordering,
    page,
    page_size: pageSize,
  });

  useEffect(() => {
    setPage(1);
  }, [typeFilter, industryFilter, relationshipTilesFilter]);
  const [createPartner, { isLoading: isCreating }] = useCreatePartnerMutation();
  const [updatePartner, { isLoading: isUpdating }] = useUpdatePartnerMutation();
  const [deletePartner, { isLoading: isDeleting }] = useDeletePartnerMutation();
  const [toggleFeatured, { isLoading: isTogglingFeatured }] =
    useTogglePartnerFeaturedMutation();
  const [presignLogoUpload, { isLoading: isPresigning }] =
    usePresignPartnerLogoUploadMutation();
  const [finalizeLogoUpload, { isLoading: isFinalizing }] =
    useFinalizePartnerLogoUploadMutation();

  const isUploading = isPresigning || isFinalizing;

  const partners = data?.results || [];
  const totalCount = data?.count || 0;

  const handleSortChange = (
    field: "name" | "type" | "industry" | "featured" | "active",
  ) => {
    if (sortBy === field) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
      } else if (sortOrder === "desc") {
        setSortBy(undefined);
        setSortOrder("asc");
      }
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
    setPage(1);
  };

  const { data: allFeaturedData } = useGetPartnersAdminQuery({
    ordering: "-is_featured,name",
    page: 1,
    page_size: 1000,
  });
  const featuredCount = useMemo(() => {
    const serverCount = (allFeaturedData?.results || []).filter(
      (p) => p.is_featured,
    ).length;

    if (editingPartner) {
      if (editingPartner.is_featured && !formData.is_featured) {
        return serverCount - 1;
      }
      if (!editingPartner.is_featured && formData.is_featured) {
        return serverCount + 1;
      }
    }

    return serverCount;
  }, [allFeaturedData, editingPartner, formData.is_featured]);

  const featuredSlotsAvailable = 5 - featuredCount;
  const canAddFeatured = featuredSlotsAvailable > 0 || formData.is_featured;

  const isLastFeatured = useMemo(() => {
    if (!editingPartner) return false;
    if (editingPartner.is_featured && formData.is_featured) {
      const otherFeaturedCount = (allFeaturedData?.results || []).filter(
        (p) => p.is_featured && p.slug !== editingPartner.slug,
      ).length;
      return otherFeaturedCount === 0;
    }
    return false;
  }, [allFeaturedData, editingPartner, formData.is_featured]);

  const handleOpenCreate = () => {
    if (!canEditPartners) return;
    setEditingPartner(null);
    setFormData(INITIAL_FORM_DATA);
    setLogoFile(null);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (partner: PartnerAdmin) => {
    if (!canEditPartners) return;
    setEditingPartner(partner);
    setFormData({
      name: partner.name,
      partner_type: partner.partner_type,
      industry_type: partner.industry_type,
      tile_description: partner.tile_description,
      details_description: partner.details_description,
      partnership_description: partner.partnership_description,
      relationship_tiles: partner.relationship_tiles,
      is_featured: partner.is_featured,
      is_active: partner.is_active,
      logo_s3_path: partner.logo_s3_path,
      logo_file: partner.logo_file,
      logo_url: partner.logo_url,
    });
    setLogoFile(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    if (!hasUnsavedChanges) {
      setIsModalOpen(false);
      setEditingPartner(null);
      setFormData(INITIAL_FORM_DATA);
      setLogoFile(null);
      return;
    }
    setLeaveDialogOpen(true);
  };

  const handleDiscardAndClose = () => {
    setLeaveDialogOpen(false);
    setIsModalOpen(false);
    setEditingPartner(null);
    setFormData(INITIAL_FORM_DATA);
    setLogoFile(null);
  };

  const generateSlug = (name: string): string => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
  };

  const buildSaveConfirmSections = (): ConfirmAccessChangesSection[] => {
    const sections: ConfirmAccessChangesSection[] = [];

    if (editingPartner) {
      const updated: string[] = [];
      const enabled: string[] = [];
      const disabled: string[] = [];
      const removed: string[] = [];
      const added: string[] = [];

      if (formData.name !== editingPartner.name) {
        updated.push(`Name: "${editingPartner.name}" → "${formData.name}"`);
      }
      if (formData.partner_type !== editingPartner.partner_type) {
        updated.push(
          `Partner Type: "${PARTNER_TYPE_DISPLAY_NAMES[editingPartner.partner_type]}" → "${PARTNER_TYPE_DISPLAY_NAMES[formData.partner_type]}"`,
        );
      }
      if (formData.industry_type !== editingPartner.industry_type) {
        updated.push(
          `Industry Type: "${INDUSTRY_DISPLAY_NAMES[editingPartner.industry_type]}" → "${INDUSTRY_DISPLAY_NAMES[formData.industry_type]}"`,
        );
      }
      if (formData.tile_description !== editingPartner.tile_description) {
        updated.push("Tile Description");
      }
      if (formData.details_description !== editingPartner.details_description) {
        updated.push("Details Description");
      }
      if (
        formData.partnership_description !==
        editingPartner.partnership_description
      )
        updated.push("Partnership Description");

      const originalTiles = new Set(editingPartner.relationship_tiles);
      const newTiles = new Set(formData.relationship_tiles);
      const addedTiles = formData.relationship_tiles.filter(
        (t) => !originalTiles.has(t),
      );
      const removedTiles = editingPartner.relationship_tiles.filter(
        (t) => !newTiles.has(t),
      );

      if (addedTiles.length > 0) {
        addedTiles.forEach((tile) => {
          const tileInfo = RELATIONSHIP_TILES.find((t) => t.key === tile);
          added.push(`Relationship Tile: "${tileInfo?.title || tile}"`);
        });
      }
      if (removedTiles.length > 0) {
        removedTiles.forEach((tile) => {
          const tileInfo = RELATIONSHIP_TILES.find((t) => t.key === tile);
          removed.push(`Relationship Tile: "${tileInfo?.title || tile}"`);
        });
      }

      if (formData.is_featured !== editingPartner.is_featured) {
        if (formData.is_featured) {
          enabled.push("Featured Status");
        } else {
          disabled.push("Featured Status");
        }
      }
      if (formData.is_active !== editingPartner.is_active) {
        if (formData.is_active) {
          enabled.push("Active Status");
        } else {
          disabled.push("Active Status");
        }
      }
      if (logoFile !== null) {
        updated.push("Logo");
      }

      if (
        updated.length > 0 ||
        enabled.length > 0 ||
        disabled.length > 0 ||
        added.length > 0 ||
        removed.length > 0
      ) {
        sections.push({
          label: "CHANGES",
          updated,
          enabled,
          disabled,
          added,
          removed,
        });
      }
    } else {
      const added: string[] = [];
      const enabled: string[] = [];

      if (formData.name.trim()) added.push("Name");
      added.push(
        `Partner Type: "${PARTNER_TYPE_DISPLAY_NAMES[formData.partner_type]}"`,
      );
      added.push(
        `Industry Type: "${INDUSTRY_DISPLAY_NAMES[formData.industry_type]}"`,
      );
      if (formData.tile_description.trim()) added.push("Tile Description");
      if (formData.details_description.trim())
        added.push("Details Description");
      if (formData.partnership_description.trim())
        added.push("Partnership Description");
      if (formData.relationship_tiles.length > 0) {
        formData.relationship_tiles.forEach((tile) => {
          const tileInfo = RELATIONSHIP_TILES.find((t) => t.key === tile);
          added.push(`Relationship Tile: "${tileInfo?.title || tile}"`);
        });
      }
      if (formData.is_featured) enabled.push("Featured Status");
      if (formData.is_active) enabled.push("Active Status");
      if (logoFile !== null) added.push("Logo");

      if (added.length > 0 || enabled.length > 0) {
        sections.push({ label: "NEW PARTNER", added, enabled });
      }
    }

    return sections;
  };

  const performSave = async () => {
    if (!canEditPartners) return;
    setSaveConfirmOpen(false);
    try {
      let logoS3Path = formData.logo_s3_path;

      if (logoFile) {
        const presign = await presignLogoUpload({
          file_name: logoFile.name,
          content_type: logoFile.type || "application/octet-stream",
        }).unwrap();

        const uploadResponse = await fetch(presign.upload_url, {
          method: "PUT",
          headers: presign.headers,
          body: logoFile,
        });

        if (!uploadResponse.ok) {
          throw new Error(`Upload failed with status ${uploadResponse.status}`);
        }

        const finalize = await finalizeLogoUpload({
          s3_path: presign.s3_path,
          file_size: logoFile.size,
          content_type: logoFile.type || "application/octet-stream",
        }).unwrap();

        logoS3Path = finalize.s3_path;
        setFormData((prev) => ({ ...prev, logo_url: finalize.url }));
      }

      const slug = editingPartner?.slug || generateSlug(formData.name);

      const submitData = {
        ...formData,
        slug,
        logo_s3_path: logoS3Path,
      };

      const wasEditing = Boolean(editingPartner);
      if (editingPartner) {
        await updatePartner({
          slug: editingPartner.slug,
          data: submitData,
        }).unwrap();
      } else {
        await createPartner(submitData).unwrap();
      }

      setEditingPartner(null);
      setFormData(INITIAL_FORM_DATA);
      setLogoFile(null);
      setIsModalOpen(false);
      refetch();
      dispatch(
        pushNotification({
          message: wasEditing
            ? "Partner updated successfully."
            : "Partner created successfully.",
          severity: "success",
        }),
      );
    } catch (error) {
      console.error("Failed to save partner:", error);
      dispatch(
        pushNotification({
          message: "Failed to save partner. Please try again.",
          severity: "error",
        }),
      );
    }
  };

  const handleSave = () => {
    if (!canEditPartners) return;
    const sections = buildSaveConfirmSections();
    if (sections.length === 0) {
      return;
    }
    setSaveConfirmOpen(true);
  };

  const handleDelete = (partner: PartnerAdmin) => {
    if (!canEditPartners) return;
    setPartnerToDelete(partner);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!canEditPartners || !partnerToDelete) return;
    try {
      await deletePartner(partnerToDelete.slug).unwrap();
      setDeleteConfirmOpen(false);
      setDeleteConfirmInput("");
      setPartnerToDelete(null);
      refetch();
      dispatch(
        pushNotification({
          message: "Partner deleted successfully.",
          severity: "success",
        }),
      );
    } catch (error) {
      console.error("Failed to delete partner:", error);
      setDeleteConfirmOpen(false);
      dispatch(
        pushNotification({
          message: "Failed to delete partner. Please try again.",
          severity: "error",
        }),
      );
    }
  };

  const handleDeleteConfirmClose = () => {
    if (!isDeleting) {
      setDeleteConfirmOpen(false);
      setDeleteConfirmInput("");
      setPartnerToDelete(null);
    }
  };

  const handleToggleFeatured = async (slug: string) => {
    if (!canEditPartners) return;
    const partner =
      (allFeaturedData?.results || []).find((p) => p.slug === slug) ??
      partners.find((p) => p.slug === slug);
    const isCurrentlyFeatured = partner?.is_featured ?? false;

    if (isCurrentlyFeatured) {
      const otherFeaturedCount = (allFeaturedData?.results || []).filter(
        (p) => p.is_featured && p.slug !== slug,
      ).length;
      if (otherFeaturedCount === 0) {
        return;
      }
    }

    if (!isCurrentlyFeatured && featuredCount >= 5) {
      setFeaturedWarningOpen(true);
      return;
    }

    try {
      await toggleFeatured(slug).unwrap();
      refetch();
      dispatch(
        pushNotification({
          message: isCurrentlyFeatured
            ? "Partner removed from featured."
            : "Partner marked as featured.",
          severity: "success",
        }),
      );
    } catch (error) {
      console.error("Failed to toggle featured:", error);
      dispatch(
        pushNotification({
          message: "Failed to update featured status. Please try again.",
          severity: "error",
        }),
      );
    }
  };

  const handleLogoFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      const filename = file.name;

      const existingLogo = (allFeaturedData?.results || []).find(
        (p) => p.logo_s3_path && p.logo_s3_path.endsWith(filename),
      );

      if (existingLogo) {
        setPendingLogoFile(file);
        setLogoDuplicateWarningOpen(true);
      } else {
        setLogoFile(file);
      }
    }
  };

  const handleLogoDuplicateClose = () => {
    setPendingLogoFile(null);
    setLogoDuplicateWarningOpen(false);
  };

  const handleLogoDuplicateConfirm = () => {
    if (pendingLogoFile) {
      setLogoFile(pendingLogoFile);
    }
    setPendingLogoFile(null);
    setLogoDuplicateWarningOpen(false);
  };

  const handleToggleRelationshipTile = (tileKey: RelationshipTileKey) => {
    setFormData((prev) => ({
      ...prev,
      relationship_tiles: prev.relationship_tiles.includes(tileKey)
        ? prev.relationship_tiles.filter((t) => t !== tileKey)
        : [...prev.relationship_tiles, tileKey],
    }));
  };

  return (
    <Layout title="Partner Management" content="Partner Management">
      <Container
        maxWidth={false}
        sx={{
          py: 3,
          width: "100%",
          boxSizing: "border-box",
          height: TABLE_PAGE_CONTAINER_HEIGHT,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {isCalibrateManager && !isSuperUser && !isPartnershipTeam && (
          <Typography variant="body2" color="text.secondary" mb={2}>
            You can view partners. Only a Super Admin or a Partnership Manager
            can make changes.
          </Typography>
        )}
        <Box mb={2} display="flex" flexDirection="column" gap={1}>
          {isMobile ? (
            <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <FileSearch
                  onSearch={(term) => {
                    setSearch(term ?? "");
                    setPage(1);
                  }}
                  placeholder="Search partners..."
                  initialValue={search}
                />
              </Box>
              <IconButton
                color="primary"
                aria-label="Open filters and actions"
                onClick={() => setMobileOptionsDrawerOpen(true)}
                sx={{
                  flexShrink: 0,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                }}
              >
                <Tune />
              </IconButton>
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Box sx={{ display: "flex", gap: 1, flex: 1, minWidth: 0 }}>
                <Box
                  sx={{
                    maxWidth: "100%",
                    minWidth: 300,
                    flexShrink: 1,
                  }}
                >
                  <FileSearch
                    onSearch={(term) => {
                      setSearch(term ?? "");
                      setPage(1);
                    }}
                    placeholder="Search partners..."
                    initialValue={search}
                  />
                </Box>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={handleOpenCreate}
                  disabled={isCreating || !canEditPartners}
                  sx={{ minWidth: "auto", px: 2 }}
                >
                  Add Partner
                </Button>
              </Box>
            </Box>
          )}
        </Box>

        {!isMobile && (
          <Box
            sx={{
              display: "flex",
              gap: 1,
              flexWrap: "wrap",
              mb: 2,
              alignItems: "center",
            }}
          >
            <FormControl size="small" sx={{ maxWidth: 120, width: "100%" }}>
              <InputLabel sx={{ fontSize: "0.75rem" }}>Partner Type</InputLabel>
              <Select
                value={typeFilter || "all"}
                label="Partner Type"
                onChange={(e) => {
                  setTypeFilter(
                    e.target.value === "all"
                      ? ""
                      : (e.target.value as PartnerType),
                  );
                  setPage(1);
                }}
                sx={{
                  "& .MuiSelect-select": {
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    py: 0.5,
                  },
                }}
              >
                <MenuItem value="all">All</MenuItem>
                {Object.entries(PARTNER_TYPE_DISPLAY_NAMES).map(
                  ([key, label]) => (
                    <MenuItem key={key} value={key}>
                      {label}
                    </MenuItem>
                  ),
                )}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ maxWidth: 170, width: "100%" }}>
              <InputLabel sx={{ fontSize: "0.75rem" }}>
                Relationship Tiles
              </InputLabel>
              <Select
                value={relationshipTilesFilter || "all"}
                label="Relationship Tiles"
                onChange={(e) => {
                  setRelationshipTilesFilter(
                    e.target.value === "all"
                      ? ""
                      : (e.target.value as RelationshipTileKey),
                  );
                  setPage(1);
                }}
                sx={{
                  "& .MuiSelect-select": {
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    py: 0.5,
                  },
                }}
              >
                <MenuItem value="all">All</MenuItem>
                {RELATIONSHIP_TILES.map((tile) => (
                  <MenuItem key={tile.key} value={tile.key}>
                    {tile.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ maxWidth: 140, width: "100%" }}>
              <InputLabel sx={{ fontSize: "0.75rem" }}>Industry</InputLabel>
              <Select
                value={industryFilter || "all"}
                label="Industry"
                onChange={(e) => {
                  setIndustryFilter(
                    e.target.value === "all"
                      ? ""
                      : (e.target.value as IndustryType),
                  );
                  setPage(1);
                }}
                sx={{
                  "& .MuiSelect-select": {
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    py: 0.5,
                  },
                }}
              >
                <MenuItem value="all">All</MenuItem>
                {Object.entries(INDUSTRY_DISPLAY_NAMES).map(([key, label]) => (
                  <MenuItem key={key} value={key}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {(typeFilter || relationshipTilesFilter || industryFilter) && (
              <Button
                size="small"
                onClick={() => {
                  setTypeFilter("");
                  setRelationshipTilesFilter("");
                  setIndustryFilter("");
                }}
                sx={{ minWidth: "auto" }}
              >
                Clear Filters
              </Button>
            )}
          </Box>
        )}

        <Paper
          elevation={0}
          sx={{
            display: "flex",
            flexDirection: "column",
            overflow: "clip",
            flex: 1,
            minHeight: 0,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            boxShadow: 1,
          }}
        >
          <CustomScrollbars
            sx={{ flex: 1, minWidth: 0, overflowX: "auto" }}
            trackOffsetTop={40}
            trackOffsetBottom={2}
          >
            <Table stickyHeader size="small" sx={{ minWidth: 700 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>
                    <TableSortLabel
                      active={sortBy === "name"}
                      direction={sortBy === "name" ? sortOrder : "asc"}
                      onClick={() => handleSortChange("name")}
                    >
                      Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    <TableSortLabel
                      active={sortBy === "type"}
                      direction={sortBy === "type" ? sortOrder : "asc"}
                      onClick={() => handleSortChange("type")}
                    >
                      Partner Type
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 300 }}>
                    Relationship Tiles
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    <TableSortLabel
                      active={sortBy === "industry"}
                      direction={sortBy === "industry" ? sortOrder : "asc"}
                      onClick={() => handleSortChange("industry")}
                    >
                      Industry
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>
                    <TableSortLabel
                      active={sortBy === "featured"}
                      direction={sortBy === "featured" ? sortOrder : "asc"}
                      onClick={() => handleSortChange("featured")}
                    >
                      Featured
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>
                    <TableSortLabel
                      active={sortBy === "active"}
                      direction={sortBy === "active" ? sortOrder : "asc"}
                      onClick={() => handleSortChange("active")}
                    >
                      Active
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 6 }).map((__, j) => (
                        <TableCell key={j}>
                          <Skeleton variant="text" width="80%" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : partners.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No partners found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  partners.map((partner) => (
                    <TableRow key={partner.id} hover>
                      <TableCell>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          {partner.logo_url ? (
                            <Box
                              component="img"
                              src={partner.logo_url}
                              alt={partner.name}
                              sx={{
                                width: 32,
                                height: 32,
                                objectFit: "contain",
                                flexShrink: 0,
                              }}
                            />
                          ) : (
                            <Box
                              sx={{ width: 32, height: 32, flexShrink: 0 }}
                            />
                          )}
                          <Typography variant="body2" fontWeight={500}>
                            {partner.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={
                            PARTNER_TYPE_DISPLAY_NAMES[partner.partner_type]
                          }
                          size="small"
                          color={
                            partner.partner_type === "service"
                              ? "primary"
                              : "secondary"
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {partner.relationship_tiles.map((tile) => {
                            const tileInfo = RELATIONSHIP_TILES.find(
                              (t) => t.key === tile,
                            );
                            return (
                              <Chip
                                key={tile}
                                label={tileInfo?.title || tile}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: "0.7rem", height: 20 }}
                              />
                            );
                          })}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "inline-flex" }}>
                          <PartnerBadge
                            type="industry"
                            value={partner.industry_type}
                            label={
                              INDUSTRY_DISPLAY_NAMES[partner.industry_type]
                            }
                            size="small"
                          />
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          onClick={() => handleToggleFeatured(partner.slug)}
                          disabled={
                            isTogglingFeatured ||
                            isFetching ||
                            (!partner.is_featured && featuredCount >= 5) ||
                            (!partner.is_active && !partner.is_featured) ||
                            (partner.is_featured &&
                              (allFeaturedData?.results || []).filter(
                                (p) => p.is_featured && p.slug !== partner.slug,
                              ).length === 0) ||
                            !canEditPartners
                          }
                          color={partner.is_featured ? "warning" : "default"}
                          size="small"
                          title={
                            !partner.is_active && !partner.is_featured
                              ? "Partner must be active to be featured"
                              : !partner.is_featured && featuredCount >= 5
                                ? "Maximum 5 featured partners reached"
                                : partner.is_featured &&
                                    (allFeaturedData?.results || []).filter(
                                      (p) =>
                                        p.is_featured &&
                                        p.slug !== partner.slug,
                                    ).length === 0
                                  ? "Cannot remove the last featured partner"
                                  : partner.is_featured
                                    ? "Remove from featured"
                                    : "Mark as featured"
                          }
                        >
                          {partner.is_featured ? (
                            <Star fontSize="small" />
                          ) : (
                            <StarBorder fontSize="small" />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={partner.is_active ? "Active" : "Inactive"}
                          size="small"
                          sx={
                            partner.is_active
                              ? {
                                  bgcolor: "rgba(46,125,50,0.10)",
                                  color: "success.main",
                                  fontWeight: 600,
                                }
                              : {
                                  bgcolor: "rgba(198, 40, 40, 0.10)",
                                  color: "error.main",
                                  fontWeight: 600,
                                }
                          }
                        />
                      </TableCell>
                      <TableCell align="right">
                        {partner.is_active ? (
                          <Tooltip title="Preview partner page">
                            <IconButton
                              href={`/partners/${partner.slug}`}
                              target="_blank"
                              size="small"
                            >
                              <OpenInNew fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Partner is inactive">
                            <span>
                              <IconButton size="small" disabled>
                                <OpenInNew fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        <IconButton
                          onClick={() => handleOpenEdit(partner)}
                          disabled={!canEditPartners}
                          size="small"
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDelete(partner)}
                          size="small"
                          color="error"
                          disabled={isDeleting || !canEditPartners}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CustomScrollbars>
          <PaginationControls
            currentLimit={pageSize}
            onLimitChange={(limit) => {
              setPageSize(limit);
              setPage(1);
            }}
            totalItems={totalCount}
            currentPage={page}
            totalPages={Math.max(1, Math.ceil(totalCount / pageSize))}
            onPageChange={setPage}
            hasNextPage={page < Math.ceil(totalCount / pageSize)}
            disabled={isLoading || isFetching}
            size="small"
            borderStyle={{ borderTop: "none" }}
            buttonStyle={{ border: "1px solid", borderColor: "divider" }}
          />
        </Paper>

        <AppDialog
          open={isModalOpen}
          onClose={handleCloseModal}
          title={
            editingPartner
              ? `Edit Partner: ${editingPartner.name}`
              : "Add New Partner"
          }
          maxWidth="md"
          showCloseIcon
          actions={
            <>
              <Button onClick={handleCloseModal}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={
                  isCreating ||
                  isUpdating ||
                  isUploading ||
                  (editingPartner ? !hasUnsavedChanges : !isFormValid)
                }
                startIcon={editingPartner ? null : <Add />}
              >
                {editingPartner ? "Update Partner" : "Create Partner"}
              </Button>
            </>
          }
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                margin="normal"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Partner Type</InputLabel>
                <Select
                  value={formData.partner_type}
                  label="Partner Type"
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      partner_type: e.target.value as PartnerType,
                    })
                  }
                >
                  {Object.entries(PARTNER_TYPE_DISPLAY_NAMES).map(
                    ([key, label]) => (
                      <MenuItem key={key} value={key}>
                        {label}
                      </MenuItem>
                    ),
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Industry Type</InputLabel>
                <Select
                  value={formData.industry_type}
                  label="Industry Type"
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      industry_type: e.target.value as IndustryType,
                    })
                  }
                >
                  {Object.entries(INDUSTRY_DISPLAY_NAMES).map(
                    ([key, label]) => (
                      <MenuItem key={key} value={key}>
                        {label}
                      </MenuItem>
                    ),
                  )}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Tile Description (Short)"
                value={formData.tile_description}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    tile_description: e.target.value,
                  })
                }
                margin="normal"
                helperText={`Shown on partner cards (${formData.tile_description.length}/50 chars)`}
                inputProps={{ maxLength: 50 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Details Description"
                value={formData.details_description}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    details_description: e.target.value,
                  })
                }
                margin="normal"
                multiline
                rows={4}
                helperText={`Full description shown on partner detail page (${formData.details_description.length}/450 chars)`}
                inputProps={{ maxLength: 450 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Partnership Description"
                value={formData.partnership_description}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    partnership_description: e.target.value,
                  })
                }
                margin="normal"
                multiline
                rows={3}
                helperText={`Description of the partnership relationship (${formData.partnership_description.length}/150 chars)`}
                inputProps={{ maxLength: 150 }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                Relationship Tiles
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {RELATIONSHIP_TILES.map((tile) => (
                  <Chip
                    key={tile.key}
                    label={tile.title}
                    onClick={() => handleToggleRelationshipTile(tile.key)}
                    color={
                      formData.relationship_tiles.includes(tile.key)
                        ? "primary"
                        : "default"
                    }
                    variant={
                      formData.relationship_tiles.includes(tile.key)
                        ? "filled"
                        : "outlined"
                    }
                    clickable
                  />
                ))}
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                Logo
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Button variant="outlined" component="label">
                  Upload Logo
                  <input
                    type="file"
                    accept="image/*,.svg"
                    hidden
                    onChange={handleLogoFileChange}
                  />
                </Button>
                {(logoFile || formData.logo_url) && (
                  <Box
                    component="img"
                    src={
                      logoFile
                        ? URL.createObjectURL(logoFile)
                        : formData.logo_url
                    }
                    alt="Logo preview"
                    sx={{
                      width: 60,
                      height: 60,
                      objectFit: "contain",
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      p: 0.5,
                    }}
                  />
                )}
              </Box>
              {logoFile && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Selected: {logoFile.name}
                </Typography>
              )}
              {formData.logo_s3_path && !logoFile && (
                <Typography
                  variant="body2"
                  sx={{ mt: 1, color: "text.secondary" }}
                >
                  Current: {formData.logo_s3_path.split("/").pop()}
                </Typography>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <Box
                sx={{ display: "flex", alignItems: "center", mt: 2, gap: 1 }}
              >
                <Switch
                  checked={formData.is_featured}
                  onChange={(e) => {
                    const newValue = e.target.checked;
                    if (
                      newValue &&
                      !formData.is_featured &&
                      featuredCount >= 5
                    ) {
                      setPendingFeaturedChange(newValue);
                      setFeaturedWarningOpen(true);
                      return;
                    }
                    if (newValue && !formData.is_active) {
                      return;
                    }
                    if (!newValue && isLastFeatured) {
                      return;
                    }
                    setFormData({
                      ...formData,
                      is_featured: newValue,
                    });
                  }}
                  disabled={
                    (!canAddFeatured && !formData.is_featured) ||
                    (!formData.is_active && !formData.is_featured) ||
                    (isLastFeatured && formData.is_featured)
                  }
                />
                <Typography>Featured Partner</Typography>
                {formData.is_featured && (
                  <Chip
                    size="small"
                    color="warning"
                    label={`${featuredCount}/5 featured`}
                    icon={<Star fontSize="small" />}
                  />
                )}
                {!formData.is_featured &&
                  !editingPartner &&
                  featuredCount >= 5 && (
                    <Chip
                      size="small"
                      color="error"
                      label="Max 5 reached"
                      icon={<Warning fontSize="small" />}
                    />
                  )}
                {!formData.is_featured &&
                  editingPartner &&
                  featuredCount >= 5 && (
                    <Chip
                      size="small"
                      color="info"
                      label={`${featuredCount}/5 featured`}
                      icon={<Star fontSize="small" />}
                    />
                  )}
              </Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 0.5, ml: 6 }}
              >
                Exactly 5 partners must be featured. Currently {featuredCount}
                /5.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                Status
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Chip
                  label="Active"
                  size="small"
                  clickable
                  onClick={() => setFormData({ ...formData, is_active: true })}
                  sx={{
                    fontWeight: 600,
                    fontSize: 12,
                    bgcolor: formData.is_active ? "success.main" : undefined,
                    color: formData.is_active ? "common.white" : undefined,
                    border: formData.is_active ? "none" : "1px solid",
                    borderColor: "divider",
                  }}
                />
                <Chip
                  label="Inactive"
                  size="small"
                  clickable
                  onClick={() => setFormData({ ...formData, is_active: false })}
                  sx={{
                    fontWeight: 600,
                    fontSize: 12,
                    bgcolor: !formData.is_active ? "error.main" : undefined,
                    color: !formData.is_active ? "common.white" : undefined,
                    border: !formData.is_active ? "none" : "1px solid",
                    borderColor: "divider",
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </AppDialog>

        <AppDialog
          open={saveConfirmOpen}
          onClose={() =>
            !isCreating &&
            !isUpdating &&
            !isUploading &&
            setSaveConfirmOpen(false)
          }
          title={
            editingPartner ? "Confirm Partner Changes" : "Confirm New Partner"
          }
          maxWidth="sm"
          actions={
            <>
              <Button
                onClick={() => setSaveConfirmOpen(false)}
                disabled={isCreating || isUpdating || isUploading}
                sx={{ textTransform: "none" }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => void performSave()}
                disabled={isCreating || isUpdating || isUploading}
                sx={{ textTransform: "none", fontWeight: 700 }}
              >
                Confirm
              </Button>
            </>
          }
        >
          {buildSaveConfirmSections().map((section, idx) => (
            <Box key={section.label}>
              {idx > 0 && <Divider sx={{ my: 2 }} />}
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={700}
                textTransform="uppercase"
                letterSpacing={0.6}
                display="block"
                sx={{ mb: 1 }}
              >
                {section.label}
              </Typography>
              {section.items && section.items.length > 0 && (
                <Box
                  component="ul"
                  sx={{ m: 0, pl: 2.5, "& li": { mb: 0.25 } }}
                >
                  {section.items.map((item) => (
                    <Typography key={item} variant="body2" component="li">
                      {item}
                    </Typography>
                  ))}
                </Box>
              )}
              {section.added && section.added.length > 0 && (
                <Box
                  sx={{
                    mb:
                      section.updated?.length ||
                      section.enabled?.length ||
                      section.disabled?.length ||
                      section.removed?.length
                        ? 1.5
                        : 0,
                  }}
                >
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    letterSpacing={0.5}
                    sx={{ color: "success.main", display: "block", mb: 0.5 }}
                  >
                    Added
                  </Typography>
                  <Box
                    component="ul"
                    sx={{ m: 0, pl: 2.5, "& li": { mb: 0.25 } }}
                  >
                    {section.added.map((item) => (
                      <Typography key={item} variant="body2" component="li">
                        {item}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}
              {section.updated && section.updated.length > 0 && (
                <Box
                  sx={{
                    mb:
                      section.enabled?.length ||
                      section.disabled?.length ||
                      section.removed?.length
                        ? 1.5
                        : 0,
                  }}
                >
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    letterSpacing={0.5}
                    sx={{ color: "info.main", display: "block", mb: 0.5 }}
                  >
                    Updated
                  </Typography>
                  <Box
                    component="ul"
                    sx={{ m: 0, pl: 2.5, "& li": { mb: 0.25 } }}
                  >
                    {section.updated.map((item) => (
                      <Typography key={item} variant="body2" component="li">
                        {item}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}
              {section.enabled && section.enabled.length > 0 && (
                <Box
                  sx={{
                    mb:
                      section.disabled?.length || section.removed?.length
                        ? 1.5
                        : 0,
                  }}
                >
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    letterSpacing={0.5}
                    sx={{ color: "success.main", display: "block", mb: 0.5 }}
                  >
                    Enabled
                  </Typography>
                  <Box
                    component="ul"
                    sx={{ m: 0, pl: 2.5, "& li": { mb: 0.25 } }}
                  >
                    {section.enabled.map((item) => (
                      <Typography key={item} variant="body2" component="li">
                        {item}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}
              {section.disabled && section.disabled.length > 0 && (
                <Box sx={{ mb: section.removed?.length ? 1.5 : 0 }}>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    letterSpacing={0.5}
                    sx={{ color: "error.main", display: "block", mb: 0.5 }}
                  >
                    Disabled
                  </Typography>
                  <Box
                    component="ul"
                    sx={{ m: 0, pl: 2.5, "& li": { mb: 0.25 } }}
                  >
                    {section.disabled.map((item) => (
                      <Typography key={item} variant="body2" component="li">
                        {item}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}
              {section.removed && section.removed.length > 0 && (
                <Box>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    letterSpacing={0.5}
                    sx={{ color: "error.main", display: "block", mb: 0.5 }}
                  >
                    Removed
                  </Typography>
                  <Box
                    component="ul"
                    sx={{ m: 0, pl: 2.5, "& li": { mb: 0.25 } }}
                  >
                    {section.removed.map((item) => (
                      <Typography key={item} variant="body2" component="li">
                        {item}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          ))}
        </AppDialog>

        <AppDialog
          open={leaveDialogOpen}
          onClose={() => setLeaveDialogOpen(false)}
          title="Unsaved Changes"
          maxWidth="sm"
          actions={
            <>
              <Button onClick={() => setLeaveDialogOpen(false)}>Stay</Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleDiscardAndClose}
                sx={{ textTransform: "none", fontWeight: 700 }}
              >
                Discard and Close
              </Button>
            </>
          }
        >
          <Typography variant="body2">
            {editingPartner
              ? "You have unsaved changes to this partner. Discard them to close or stay to keep editing."
              : "You have started creating a new partner. Discard this draft to close or stay to keep editing."}
          </Typography>
        </AppDialog>

        <AppDialog
          open={logoDuplicateWarningOpen}
          onClose={handleLogoDuplicateClose}
          title="Duplicate Logo Filename"
          maxWidth="sm"
          actions={
            <>
              <Button onClick={handleLogoDuplicateClose}>Cancel</Button>
              <Button variant="contained" onClick={handleLogoDuplicateConfirm}>
                Replace
              </Button>
            </>
          }
        >
          <Typography variant="body2" sx={{ mb: 1 }}>
            A logo with the filename <strong>"{pendingLogoFile?.name}"</strong>{" "}
            already exists in data-storage.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Do you want to overwrite the existing file with this new logo?
          </Typography>
        </AppDialog>

        <AppDialog
          open={deleteConfirmOpen}
          onClose={handleDeleteConfirmClose}
          title={`Delete Partner: ${partnerToDelete?.name ?? ""}`}
          maxWidth="sm"
          actions={
            <>
              <Button
                onClick={handleDeleteConfirmClose}
                disabled={isDeleting}
                sx={{ textTransform: "none" }}
              >
                Cancel
              </Button>
              <Button
                color="error"
                variant="contained"
                onClick={handleDeleteConfirm}
                disabled={
                  isDeleting ||
                  !canEditPartners ||
                  (partnerToDelete?.name ?? "").trim() !==
                    deleteConfirmInput.trim()
                }
                sx={{ textTransform: "none", fontWeight: 700 }}
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </Button>
            </>
          }
        >
          <Typography
            variant="subtitle2"
            fontWeight={800}
            sx={{ color: "error.main", textTransform: "uppercase", mb: 1.5 }}
          >
            Warning
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            This action cannot be undone. The partner{" "}
            <strong>"{partnerToDelete?.name ?? ""}"</strong> and all associated
            data will be permanently removed.
          </Typography>
          <TextField
            label="Type partner name to confirm"
            placeholder={partnerToDelete?.name ?? ""}
            value={deleteConfirmInput}
            onChange={(e) => setDeleteConfirmInput(e.target.value)}
            size="small"
            fullWidth
            autoComplete="off"
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
          />
        </AppDialog>

        <Dialog
          open={featuredWarningOpen}
          onClose={() => {
            setFeaturedWarningOpen(false);
            setPendingFeaturedChange(null);
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Warning color="warning" />
            Maximum Featured Partners Reached
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 2 }}>
              You already have <strong>{featuredCount}</strong> featured
              partners (maximum is 5).
            </Typography>
            <Typography variant="body2" color="text.secondary">
              To add this partner as featured, you must first remove another
              featured partner. Go back to the list and un-feature an existing
              partner, then return to add this one.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setFeaturedWarningOpen(false);
                setPendingFeaturedChange(null);
              }}
              variant="contained"
            >
              I Understand
            </Button>
          </DialogActions>
        </Dialog>

        <Drawer
          anchor="bottom"
          open={isMobile && mobileOptionsDrawerOpen}
          onClose={() => setMobileOptionsDrawerOpen(false)}
          PaperProps={{
            sx: {
              borderTopLeftRadius: 2,
              borderTopRightRadius: 2,
              maxHeight: "min(88vh, 560px)",
              px: 2,
              py: 2,
            },
          }}
        >
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
            Filters & Actions
          </Typography>

          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              handleOpenCreate();
              setMobileOptionsDrawerOpen(false);
            }}
            disabled={isCreating || !canEditPartners}
            fullWidth
            sx={{ mb: 3, py: 1.5 }}
          >
            Add Partner
          </Button>

          <Divider sx={{ mb: 2 }} />

          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
            Filters
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Partner Type</InputLabel>
              <Select
                value={typeFilter || "all"}
                label="Partner Type"
                onChange={(e) => {
                  setTypeFilter(
                    e.target.value === "all"
                      ? ""
                      : (e.target.value as PartnerType),
                  );
                  setPage(1);
                }}
              >
                <MenuItem value="all">All</MenuItem>
                {Object.entries(PARTNER_TYPE_DISPLAY_NAMES).map(
                  ([key, label]) => (
                    <MenuItem key={key} value={key}>
                      {label}
                    </MenuItem>
                  ),
                )}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel>Relationship Tiles</InputLabel>
              <Select
                value={relationshipTilesFilter || "all"}
                label="Relationship Tiles"
                onChange={(e) => {
                  setRelationshipTilesFilter(
                    e.target.value === "all"
                      ? ""
                      : (e.target.value as RelationshipTileKey),
                  );
                  setPage(1);
                }}
              >
                <MenuItem value="all">All</MenuItem>
                {RELATIONSHIP_TILES.map((tile) => (
                  <MenuItem key={tile.key} value={tile.key}>
                    {tile.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel>Industry</InputLabel>
              <Select
                value={industryFilter || "all"}
                label="Industry"
                onChange={(e) => {
                  setIndustryFilter(
                    e.target.value === "all"
                      ? ""
                      : (e.target.value as IndustryType),
                  );
                  setPage(1);
                }}
              >
                <MenuItem value="all">All</MenuItem>
                {Object.entries(INDUSTRY_DISPLAY_NAMES).map(([key, label]) => (
                  <MenuItem key={key} value={key}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {(typeFilter || relationshipTilesFilter || industryFilter) && (
              <Button
                variant="outlined"
                onClick={() => {
                  setTypeFilter("");
                  setRelationshipTilesFilter("");
                  setIndustryFilter("");
                }}
                sx={{ mt: 1 }}
              >
                Clear Filters
              </Button>
            )}
          </Box>
        </Drawer>
      </Container>
    </Layout>
  );
}
